import { Helmet } from "react-helmet";
import TopSelectedPropertyDisplay from "./_topdisplayData.jsx";
import PropertyFilter from "./_selectedpropertyfilter.jsx";
import { useState, useEffect } from "react";
import MonthYearFilterModal from "../dashboard/_monthyearfilter.jsx";
import TopMetricDetails from "./_topmetricdetails.jsx";
import { useGetApartmentDefaultersQuery, useGetRentSummaryPropertyQuery, useGetPropertyDetailsQuery } from '../../lib/apiSlice/property/propertySlice.tsx';
import { useParams } from "react-router-dom";
import AddUnitModal from "./_addUnitModal.jsx";
import HousesDisplay from "./_housesdisplay.jsx";
import { useGetUnitDetailsQuery, useSaveUnitMutation } from "../../lib/apiSlice/unit/unitSlice.tsx";
import PropertySideMetrics from "./_propertysidemetrics.jsx";
import { toast } from "react-toastify";
import SendReceiptsModal from "./_sendReceiptsModal.jsx";
import axios from 'axios';
import { serverUrl } from "../../../../serverUrl.js";

function generateRandom15DigitNumber() {
    return Math.floor(1e14 + Math.random() * 9e14).toString();
}

export default function PropertyId() {
    const { id } = useParams()
    const { data: propertyDetails } = useGetPropertyDetailsQuery({ id: id });
    
    const [currentFilter, setCurrentFilter] = useState({
        month: new Date().toLocaleString('default', { month: 'long' }).toLowerCase(),
        year: new Date().getFullYear()
    });

    // Fetch unit details with filter parameters
    const { data: unitDetails } = useGetUnitDetailsQuery({ 
        apartmentId: id,
        month: currentFilter.month,
        year: currentFilter.year
    });

    const [addUnit, { isLoading: isAddingUnit }] = useSaveUnitMutation();

    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [addUnitModalOpen, setAddUnitModalOpen] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptTargetGroup, setReceiptTargetGroup] = useState('all');
    const [receiptMonth, setReceiptMonth] = useState(new Date().toLocaleString('default', { month: 'long' }).toLowerCase());
    const [receiptYear, setReceiptYear] = useState(new Date().getFullYear());
    const [isSendingReceipts, setIsSendingReceipts] = useState(false);
    
    const [receiptCount, setReceiptCount] = useState(0);
    const [isCalculatingReceipts, setIsCalculatingReceipts] = useState(false);

    // Fetch receipt count dynamically using dry_run
    useEffect(() => {
        if (!showReceiptModal) return;
        const fetchReceiptCount = async () => {
            setIsCalculatingReceipts(true);
            try {
                const response = await serverUrl.post('/landlord/send-apartment-receipts/', {
                    apartment_id: id,
                    target_group: receiptTargetGroup,
                    target_month: receiptMonth,
                    target_year: receiptYear,
                    dry_run: true
                }, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('authTokens') ? JSON.parse(localStorage.getItem('authTokens')).access : ''}` }
                });
                setReceiptCount(response.data.count);
            } catch (error) {
                setReceiptCount(0);
            } finally {
                setIsCalculatingReceipts(false);
            }
        };
        fetchReceiptCount();
    }, [showReceiptModal, receiptTargetGroup, receiptMonth, receiptYear, id]);

    const handleSendReceipts = () => {
        const token = localStorage.getItem('token');
        setIsSendingReceipts(true);
        axios.post(`${serverUrl}/api/landlord/send-apartment-receipts/`, {
            apartment_id: id,
            target_group: receiptTargetGroup,
            target_month: receiptMonth,
            target_year: receiptYear
        }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        .then((response) => {
            setIsSendingReceipts(false);
            setShowReceiptModal(false);
            toast.success(response.data.message);
        })
        .catch((error) => {
            setIsSendingReceipts(false);
            toast.error(error.response?.data?.error || 'Failed to send receipts');
        });
    };

    const initialUnitForm = {
        house_number: "",
        rent: 0,
        service_charge: 0,
        payment_type: "rent",
        vat_included: false,
        vat_option: "none",
        vat_amount: "",
        generate_invoice_schedule: "monthly",
        due_date: "",
        bedroom: 1,
        bathroom: 1,
        total_rooms: 1,
        deposit: 0,
        token_prepaid_account_number: generateRandom15DigitNumber(),
        about: "",
        occupancy_status: "vacant",
        apartment: id,
    };
    const [unitForm, setUnitForm] = useState(initialUnitForm);

    const handleUnitChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUnitForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleUnitSave = async () => {
        try {
            const { vat_option, ...unitData } = unitForm;
            unitData.vat_included = vat_option !== "none";
            if (vat_option !== "custom") delete unitData.vat_amount;
            await addUnit(unitData).unwrap();
            toast.success('Successfully saved the unit')
            setAddUnitModalOpen(false);
            setUnitForm(initialUnitForm);
        } catch (error) {
            console.error('Failed to save unit:', error);
            const details = error?.data;
            const message = typeof details === "string"
                ? details
                : details?.rent?.[0] || details?.service_charge?.[0] || details?.non_field_errors?.[0];
            toast.error(message || 'Failed to save unit');
        }
    };

    // Filtering the houses with occupancy_status set as "occupied"
    const occupiedHouses = unitDetails?.results?.filter(houses => houses.status === 'occupied') || [];
    const numberOfOccupiedHouses = occupiedHouses.length;

    const { data: defaultersData } = useGetApartmentDefaultersQuery({ 
        id: id,
        month: currentFilter.month,
        year: currentFilter.year
    });

    // Pass the data for the property metrics
    const { data: dashboardData } = useGetRentSummaryPropertyQuery({
        month: currentFilter.month,
        year: currentFilter.year,
        id: id,
    });

    const handleFilterApply = (month, year) => {
        setCurrentFilter({ month, year });
        setIsFilterModalOpen(false);
    };

    const [houseFilters, setHouseFilters] = useState({
        search: '',
        rentAmount: '',
        paymentStatus: '',
        status: 'all',
        unitStatus:''
    });
   
    const [viewMode, setViewMode] = useState("card");
    return (
        <div>
            <Helmet
                title="Property Details"
                meta={[
                    { name: "description", content: "Details of the selected property" },
                ]}
            />

            <TopSelectedPropertyDisplay 
                propertyDetails={propertyDetails} 
                unitDetails={unitDetails} 
                houseFilters={houseFilters}
                onHouseFilterChange={setHouseFilters}

            />

            {/* Filter data */}
            <PropertyFilter
                onFilterClick={() => setIsFilterModalOpen(true)}
                currentFilter={currentFilter} 
            />

            {/* Filter modal */}
            <MonthYearFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApply={handleFilterApply}
                currentFilter={currentFilter}
                setCurrentFilter={setCurrentFilter}
            />

            <TopMetricDetails 
                dataMetrics={dashboardData} 
                defaulters={defaultersData} 
            />

            {/* Add unit button */}
            <div className='flex justify-end items-end mt-3 gap-2'>
                <button
                    onClick={() => {
                        setUnitForm({
                            ...initialUnitForm,
                            token_prepaid_account_number: generateRandom15DigitNumber()
                        });
                        setAddUnitModalOpen(true);
                    }}
                    className="bg-blue-500 hover:bg-blue-600 mt-1 mb-3 px-4 py-2 rounded-md text-white transition-colors"
                >
                    Add Unit
                </button>
                <button
                    onClick={() => setShowReceiptModal(true)}
                    className="bg-gray-500 hover:bg-gray-600 mt-1 mb-3 px-4 py-2 rounded-md text-white transition-colors"
                >
                    Send Receipts
                </button>
            </div>

            {/* Add unit modal */}
            <AddUnitModal
                show={addUnitModalOpen}
                onClose={() => setAddUnitModalOpen(false)}
                form={unitForm}
                propertyType={propertyDetails?.results[0]?.property_type}
                onChange={handleUnitChange}
                onSave={handleUnitSave}
            />

            {/* Send Receipts modal */}
            <SendReceiptsModal
                show={showReceiptModal}
                onClose={() => setShowReceiptModal(false)}
                onSend={handleSendReceipts}
                isSending={isSendingReceipts}
                isCalculating={isCalculatingReceipts}
                receiptTargetGroup={receiptTargetGroup}
                setReceiptTargetGroup={setReceiptTargetGroup}
                receiptMonth={receiptMonth}
                setReceiptMonth={setReceiptMonth}
                receiptYear={receiptYear}
                setReceiptYear={setReceiptYear}
                receiptCount={receiptCount}
                propertyDetails={propertyDetails}
            />

            {/* Display the houses and the property amenities */}
            <div className="gap-4 grid grid-cols-1 lg:grid-cols-12 mt-4 mb-2">
                <div className="lg:col-span-9">
                    <HousesDisplay 
                        unitDetails={unitDetails?.results} 
                        currentFilter={currentFilter}
                        houseFilters={houseFilters}
                        onHouseFilterChange={setHouseFilters}
                    />
                </div>

                <div className="lg:col-span-3">
                    <PropertySideMetrics
                        numberOfOccupiedHouses={numberOfOccupiedHouses}
                        houseLength={unitDetails?.results?.length}
                        selectedProperty={propertyDetails}
                        currentFilter={currentFilter}
                    />
                </div>
            </div>
        </div>
    );
}
